import { ref, watch, onBeforeUnmount } from 'vue'
import Swal from 'sweetalert2'

const UMBRAL_SEGUNDOS = 60 * 60 // 1 hora

// Mismo patrón de toast que CargaSemanal.vue - ver Toast.fire() ahí.
const Toast = Swal.mixin({
  toast: true,
  position: 'center',
  showConfirmButton: false,
  timer: 4000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.onmouseenter = Swal.stopTimer
    toast.onmouseleave = Swal.resumeTimer
  }
})

function formatHHMMSS(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = Math.floor(totalSeconds % 60)
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

function notificationSoportada() {
  return typeof Notification !== 'undefined'
}

/**
 * Aviso cada 1 hora (UMBRAL_SEGUNDOS) mientras el cronómetro de Carga Diaria sigue corriendo
 * (por si el usuario se olvidó de poner Stop).
 *
 * Usa la API de Notificaciones del navegador (`Notification`) para que el
 * aviso llegue aunque la pestaña esté minimizada o en background - funciona
 * mientras el navegador siga abierto, aunque no esté en primer plano. Si el
 * usuario no concede el permiso (o el navegador no soporta la API), cae a un
 * toast dentro de la página, que solo se ve si la pestaña está visible en
 * ese momento - ese es el único fallback posible sin el permiso.
 *
 * NO hay forma de avisar si el usuario cierra el navegador por completo -
 * eso requeriría push notifications del lado servidor, fuera de alcance.
 *
 * IMPORTANTE sobre el pedido de permiso: varios navegadores (Chrome en
 * particular) solo lo conceden de forma confiable si `requestPermission()` se
 * llama bien "pegado" al gesto del usuario (el click de Play), no desde un
 * watcher de Vue que corre en un microtask después - por eso `requestPermiso()`
 * está pensada para llamarse directo desde el handler del click, no sola.
 */
export function useRunningReminder(elapsedSeconds, isRunning) {
  let ultimoUmbralAvisado = 0

  // Estado de permiso expuesto para poder mostrar un indicador en la UI -
  // sin esto, un permiso denegado/no concedido falla en silencio y no hay
  // forma de que el usuario se entere de por qué no le llegó el aviso.
  const permiso = ref(notificationSoportada() ? Notification.permission : 'unsupported')

  // BUG (reportado): la campanita a veces no aparecía/desaparecía al revocar
  // o conceder el permiso desde la configuración del navegador, sin recargar
  // la página. Causa: `permiso` se leía una sola vez al montar y solo se
  // volvía a sincronizar cuando el USUARIO clickeaba Play o la campanita
  // (dentro de requestPermiso) - un cambio hecho por fuera de la app (browser
  // settings, u otra pestaña) dejaba el ref desactualizado hasta el próximo
  // reload. Acá se lo mantiene sincronizado en vivo:
  //   1. Permissions API (`navigator.permissions.query`) - dispara un evento
  //      'change' apenas el permiso cambia de verdad, sin importar quién lo
  //      cambió ni si la pestaña está en foco. Soportado en Chrome/Edge.
  //   2. Fallback (Firefox/Safari, que no soportan 'notifications' en la
  //      Permissions API): re-chequear al volver a foco la pestaña
  //      (visibilitychange) - cubre el caso común de "lo cambié en otra
  //      pestaña/configuración y volví a esta".
  let permissionStatus = null
  function onPermissionStatusChange() {
    permiso.value = permissionStatus.state === 'prompt' ? 'default' : permissionStatus.state
  }

  if (notificationSoportada() && navigator.permissions?.query) {
    navigator.permissions.query({ name: 'notifications' })
      .then((status) => {
        permissionStatus = status
        permiso.value = status.state === 'prompt' ? 'default' : status.state
        status.addEventListener('change', onPermissionStatusChange)
      })
      .catch(() => {
        // Navegador sin soporte real pese a exponer la API (o falla el
        // query) - queda el fallback de visibilitychange de abajo.
      })
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'visible' && notificationSoportada()) {
      permiso.value = Notification.permission
    }
  }
  document.addEventListener('visibilitychange', onVisibilityChange)

  onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange)
    if (permissionStatus) permissionStatus.removeEventListener('change', onPermissionStatusChange)
  })

  // `explicar` distingue el click de Play (silencioso: solo intenta pedir el
  // permiso si corresponde, sin interrumpir con un popup en cada Play mientras
  // esté denegado) del click en la campanita (ahí sí, el usuario está pidiendo
  // explícitamente reactivarlo, y merece la explicación de qué hacer).
  function requestPermiso({ explicar = false } = {}) {
    if (!notificationSoportada()) return

    // Una vez que el permiso quedó "denied", NINGÚN sitio puede volver a
    // mostrar el diálogo nativo por JS - es una restricción del navegador,
    // no algo que se pueda forzar. Antes acá no pasaba nada visible al
    // clickear la campanita (solo releía el mismo valor 'denied' de nuevo);
    // ahora, si el click vino de la campanita, se le explica al usuario que
    // tiene que reactivarlo a mano.
    if (Notification.permission === 'denied') {
      permiso.value = 'denied'
      if (explicar) {
        Swal.fire({
          icon: 'warning',
          title: 'Notificaciones bloqueadas',
          html: 'El navegador no deja reactivarlas desde acá. Hacé click en el ícono al lado de la URL (🔒 o ⓘ) → Permisos del sitio → Notificaciones → Permitir, y volvé a intentar.'
        })
      }
      return
    }

    if (Notification.permission === 'default') {
      Notification.requestPermission().then((p) => { permiso.value = p })
    } else {
      permiso.value = Notification.permission
    }
  }

  function avisar() {
    const cuerpo = `Llevás ${formatHHMMSS(elapsedSeconds.value)} sin poner Stop en Carga Diaria.`

    if (notificationSoportada() && Notification.permission === 'granted') {
      try {
        const n = new Notification('Carga Diaria — Cronómetro activo', {
          body: cuerpo,
          tag: 'carga-diaria-timer'
        })
        n.onclick = () => window.focus()
        return
      } catch (err) {
        // Algunos navegadores (ej. Chrome mobile) no soportan el
        // constructor Notification directo - cae al toast de abajo.
        console.warn('No se pudo mostrar la notificación nativa:', err)
      }
    }

    // Sin permiso concedido (denegado, o API no soportada/falló): mejor
    // esfuerzo, solo se ve si la pestaña está visible en este momento.
    Toast.fire({ icon: 'info', title: cuerpo })
  }

  watch(isRunning, (running) => {
    if (!running) ultimoUmbralAvisado = 0
  })

  watch(elapsedSeconds, (seg) => {
    if (!isRunning.value) return
    const umbralActual = Math.floor(seg / UMBRAL_SEGUNDOS)
    if (umbralActual > 0 && umbralActual > ultimoUmbralAvisado) {
      ultimoUmbralAvisado = umbralActual
      avisar()
    }
  })

  return { permiso, requestPermiso }
}
